import React, { createElement, useEffect, useMemo, useState } from 'react';
import { isNull } from 'lodash';
import moment from 'moment';

import DayList from './DayList';
import MonthSelect from './MonthSelect';
import ISelectDatepicker from './SelectDatepicker';
import YearSelect from './YearSelect';

export enum ETabDataPicker {
  YEAR = 'YEAR',
  MONTH = 'MONTH',
  DAY = 'DAY',
}

const tabs = {
  [ETabDataPicker.YEAR]: {
    title: 'Year',
    className: 'tw-grid tw-grid-cols-4 tw-gap-5',
    Component: YearSelect,
  },
  [ETabDataPicker.MONTH]: {
    title: 'Month',
    className: 'tw-grid tw-grid-cols-3 tw-gap-5',

    Component: MonthSelect,
  },
  [ETabDataPicker.DAY]: {
    title: 'Day',
    className: 'tw-flex tw-flex-col',
    Component: DayList,
  },
};

export interface ITabSelected {
  [ETabDataPicker.YEAR]: boolean;
  [ETabDataPicker.MONTH]: boolean;
  [ETabDataPicker.DAY]: boolean;
}

const initialStateTabSelected = {
  [ETabDataPicker.YEAR]: true,
  [ETabDataPicker.MONTH]: true,
  [ETabDataPicker.DAY]: true,
};

const SelectDatePicker = ({
  minDate = moment(new Date()).format('YYYY-MM-DD'),
  selectedDate,
  onChange,
  isMaxDateCurrent = false,
  hasError = false,
  name,
  format = ['year', 'month', 'day'],
  value,
}: ISelectDatepicker.IProps) => {
  const [tabActive, onChangeTabActive] = useState<ETabDataPicker>(ETabDataPicker.YEAR);
  const [dateSelected, onChangeSelectDate] = useState(
    (moment(value).isValid() && moment(value, 'YYYY-MM-DD')) || moment(new Date()),
  );

  const [tabSelected, onChangeTabSelected] = useState(initialStateTabSelected);
  const { Component, className } = tabs[tabActive];
  const isMaxDateCurrentState = name === 'manufacture_time';
  const handleChange = (tab: ETabDataPicker, value: string | number) => {
    switch (tab) {
      case ETabDataPicker.YEAR: {
        onChangeSelectDate((prevState) => moment(prevState).year(value as number));
        onChangeTabSelected((prevState) => ({
          [ETabDataPicker.YEAR]: true,
          [ETabDataPicker.MONTH]: false,
          [ETabDataPicker.DAY]: false,
        }));
        if (format.includes('month')) {
          onChangeTabActive(ETabDataPicker.MONTH);
        }
        break;
      }

      case ETabDataPicker.MONTH: {
        onChangeSelectDate((prevState) => moment(prevState).month(value as number));
        onChangeTabSelected((prevState) => ({
          [ETabDataPicker.YEAR]: true,
          [ETabDataPicker.MONTH]: true,
          [ETabDataPicker.DAY]: false,
        }));
        if (format.includes('day')) {
          onChangeTabActive(ETabDataPicker.DAY);
        }
        break;
      }
      case ETabDataPicker.DAY: {
        onChangeSelectDate((prevState) => moment(prevState).date(value as number));
        break;
      }
    }
    onChangeTabSelected((prevState) => ({ ...prevState, [tab]: true }));
  };

  const genDate = useMemo(() => {
    if (
      !tabSelected[ETabDataPicker.DAY] &&
      !tabSelected[ETabDataPicker.MONTH] &&
      !tabSelected[ETabDataPicker.YEAR]
    ) {
      return 'Please Select Year';
    } else if (!tabSelected[ETabDataPicker.MONTH]) {
      return dateSelected.format('YYYY');
    } else if (!tabSelected[ETabDataPicker.DAY]) {
      return dateSelected.format('YYYY-MMMM');
    }

    if (!format.includes('day')) {
      return dateSelected.format('YYYY-MMMM');
    }
    return dateSelected.format('YYYY-MMMM-DD');
  }, [tabSelected, dateSelected]);

  useEffect(() => {
    if (
      (tabSelected[ETabDataPicker.DAY] &&
        tabSelected[ETabDataPicker.MONTH] &&
        tabSelected[ETabDataPicker.YEAR]) ||
      (isMaxDateCurrentState &&
        tabSelected[ETabDataPicker.MONTH] &&
        tabSelected[ETabDataPicker.YEAR])
    ) {
      onChange({ target: { name, value: moment(dateSelected).format('YYYY-MM-DD') } });
    }
  }, [dateSelected]);

  useEffect(() => {
    if (format) {
      const updatedState = [{ YEAR: true, MONTH: false, DAY: true }]; // Your array
      const newState = updatedState.reduce((prevState, item) => {
        return {
          YEAR: item.YEAR !== undefined ? item.YEAR : prevState.YEAR,
          MONTH: item.MONTH !== undefined ? item.MONTH : prevState.MONTH,
          DAY: item.DAY !== undefined ? item.DAY : prevState.DAY,
        };
      }, initialStateTabSelected);

      onChangeTabSelected(newState);
    }
  }, []);

  useEffect(() => {
    if (value) {
      onChangeSelectDate(
        (moment(value).isValid() && moment(value, 'YYYY-MM-DD')) || moment(new Date()),
      );
      onChangeTabSelected(initialStateTabSelected);
    }
  }, [value]);

  return (
    <div className="tw-flex tw-justify-center ">
      <div
        className={`md:tw-w-[300px] tw-w-full  ${
          hasError ? 'tw-border-red500' : 'tw-border-gray900'
        } tw-flex tw-flex-col tw-border tw-rounded-l tw-items-center tw-justify-center`}
      >
        <div className="tw-bg-gray900 tw-w-full tw-p-3">
          <h6 className="tw-text-gray0 tw-text-center tw-text-[14px]">{genDate}</h6>
        </div>

        <div className=" tw-flex  tw-justify-around tw-py-3 tw-border-b tw-border-gray300 tw-w-full">
          {Object.entries(tabs).map(([tabsKey, tab]) => {
            const convertedTabsKey: string = tabsKey.toLowerCase();

            return (
              format.includes(convertedTabsKey as ISelectDatepicker.TFormat) && (
                <div
                  key={tabsKey}
                  className={`tw-text-[14px] tw-cursor-pointer ${
                    (tabsKey === tabActive && 'tw-text-green500 tw-font-[600]') || 'tw-text-gray100'
                  } `}
                  onClick={() => onChangeTabActive(tabsKey as ETabDataPicker)}
                >
                  {tab.title}
                </div>
              )
            );
          })}
        </div>

        <div className={`tw-w-full tw-p-3 tw-mt-2 tw-h-[260px] tw-overflow-y-auto ${className}`}>
          {createElement(Component, {
            handleChange,
            dateSelected,
            tabSelected,
            isMaxDateCurrent: isMaxDateCurrentState,
            minDate: moment(minDate, 'YYYY-MM-DD').format('YYYY-MM-DD'),
          })}
        </div>
        {hasError && (
          <div className="tw-text-red500 tw-flex tw-flex-col">
            The selected date is not complete
            <ul className="tw-list-disc tw-pl-3">
              {!tabSelected[ETabDataPicker.YEAR] && <li>Selected year</li>}
              {!tabSelected[ETabDataPicker.MONTH] && <li>Selected month</li>}
              {!tabSelected[ETabDataPicker.DAY] && <li>Selected day</li>}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export { SelectDatePicker };
